// Geometry (Angles, Parallel Lines, Triangles and Quadrilaterals) Knowledge Graph Samples
// Organized by difficulty levels with systematic naming

export const geometrySamples = {
  fundamental: [
    {
      id: 'geometry_fund_01',
      problem: "Find the measure of angle x if two parallel lines are cut by a transversal and angle x is alternate interior to a 60° angle",
      problemType: "geometry",
      difficulty: "fundamental",
      description: "Alternate interior angles",
      graphData: {
        nodes: [
          {
            id: "parallel_lines",
            name: "Parallel Lines",
            type: "concepts",
            description: "Two lines that never intersect",
            value: "Lines l and m are parallel",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "transversal",
            name: "Transversal",
            type: "concepts",
            description: "A line that intersects two parallel lines",
            value: "Line t intersects l and m",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "alternate_interior",
            name: "Alternate Interior Angles",
            type: "concepts",
            description: "Angles on opposite sides of transversal, between parallel lines",
            value: "Alternate interior angles are equal",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "given_angle",
            name: "Given Angle",
            type: "values",
            description: "60° angle",
            value: "60°",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "angle_x",
            name: "Angle x",
            type: "values",
            description: "The angle to find",
            value: "60°",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "alternate_interior_theorem",
            name: "Alternate Interior Angles Theorem",
            type: "formulas",
            description: "Alternate interior angles are equal when lines are parallel",
            value: "If l || m, then alternate interior angles are equal",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "identify_parallel_lines",
            name: "Identify Parallel Lines",
            type: "steps",
            description: "Recognize that the lines are parallel",
            value: "Step 1: Lines l and m are parallel",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "identify_alternate_interior",
            name: "Identify Alternate Interior Angles",
            type: "steps",
            description: "Recognize the angle relationship",
            value: "Step 2: Angle x is alternate interior to 60° angle",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_theorem",
            name: "Apply Theorem",
            type: "steps",
            description: "Use the alternate interior angles theorem",
            value: "Step 3: Alternate interior angles are equal",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "find_angle",
            name: "Find Angle Measure",
            type: "steps",
            description: "Set the angles equal and solve",
            value: "Step 4: x = 60°",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "parallel_lines", to: "transversal", type: "intersected by", strength: 9 },
          { from: "transversal", to: "alternate_interior", type: "creates", strength: 9 },
          { from: "alternate_interior", to: "given_angle", type: "related to", strength: 9 },
          { from: "given_angle", to: "angle_x", type: "equals", strength: 10 },
          { from: "alternate_interior_theorem", to: "angle_x", type: "gives", strength: 10 },
          { from: "identify_parallel_lines", to: "identify_alternate_interior", type: "enables", strength: 8 },
          { from: "identify_alternate_interior", to: "apply_theorem", type: "enables", strength: 9 },
          { from: "apply_theorem", to: "find_angle", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'geometry_fund_02',
      problem: "Find the measure of angle y if two parallel lines are cut by a transversal and angle y is corresponding to a 45° angle",
      problemType: "geometry",
      difficulty: "fundamental",
      description: "Corresponding angles",
      graphData: {
        nodes: [
          {
            id: "parallel_lines",
            name: "Parallel Lines",
            type: "concepts",
            description: "Two lines that never intersect",
            value: "Lines l and m are parallel",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "transversal",
            name: "Transversal",
            type: "concepts",
            description: "A line that intersects two parallel lines",
            value: "Line t intersects l and m",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "corresponding_angles",
            name: "Corresponding Angles",
            type: "concepts",
            description: "Angles in same position relative to transversal",
            value: "Corresponding angles are equal",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "given_angle",
            name: "Given Angle",
            type: "values",
            description: "45° angle",
            value: "45°",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "angle_y",
            name: "Angle y",
            type: "values",
            description: "The angle to find",
            value: "45°",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "corresponding_angles_theorem",
            name: "Corresponding Angles Theorem",
            type: "formulas",
            description: "Corresponding angles are equal when lines are parallel",
            value: "If l || m, then corresponding angles are equal",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "identify_parallel_lines",
            name: "Identify Parallel Lines",
            type: "steps",
            description: "Recognize that the lines are parallel",
            value: "Step 1: Lines l and m are parallel",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "identify_corresponding_angles",
            name: "Identify Corresponding Angles",
            type: "steps",
            description: "Recognize the angle relationship",
            value: "Step 2: Angle y is corresponding to 45° angle",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_theorem",
            name: "Apply Theorem",
            type: "steps",
            description: "Use the corresponding angles theorem",
            value: "Step 3: Corresponding angles are equal",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "find_angle",
            name: "Find Angle Measure",
            type: "steps",
            description: "Set the angles equal and solve",
            value: "Step 4: y = 45°",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "parallel_lines", to: "transversal", type: "intersected by", strength: 9 },
          { from: "transversal", to: "corresponding_angles", type: "creates", strength: 9 },
          { from: "corresponding_angles", to: "given_angle", type: "related to", strength: 9 },
          { from: "given_angle", to: "angle_y", type: "equals", strength: 10 },
          { from: "corresponding_angles_theorem", to: "angle_y", type: "gives", strength: 10 },
          { from: "identify_parallel_lines", to: "identify_corresponding_angles", type: "enables", strength: 8 },
          { from: "identify_corresponding_angles", to: "apply_theorem", type: "enables", strength: 9 },
          { from: "apply_theorem", to: "find_angle", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'geometry_fund_03',
      problem: "Find the third angle of a triangle if two angles measure 30° and 60°",
      problemType: "geometry",
      difficulty: "fundamental",
      description: "Triangle angle sum",
      graphData: {
        nodes: [
          {
            id: "triangle",
            name: "Triangle",
            type: "concepts",
            description: "A polygon with three sides",
            value: "Triangle ABC",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "angle_sum",
            name: "Angle Sum",
            type: "concepts",
            description: "Sum of interior angles of a triangle",
            value: "Sum = 180°",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "angle1",
            name: "First Angle",
            type: "values",
            description: "30°",
            value: "30°",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "angle2",
            name: "Second Angle",
            type: "values",
            description: "60°",
            value: "60°",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "third_angle",
            name: "Third Angle",
            type: "values",
            description: "The angle to find",
            value: "90°",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "triangle_angle_sum_formula",
            name: "Triangle Angle Sum Formula",
            type: "formulas",
            description: "Sum of interior angles of a triangle is 180°",
            value: "∠A + ∠B + ∠C = 180°",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "identify_known_angles",
            name: "Identify Known Angles",
            type: "steps",
            description: "Recognize the given angles",
            value: "Step 1: ∠A = 30°, ∠B = 60°",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "apply_angle_sum",
            name: "Apply Angle Sum Formula",
            type: "steps",
            description: "Use the triangle angle sum formula",
            value: "Step 2: 30° + 60° + ∠C = 180°",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "solve_for_third_angle",
            name: "Solve for Third Angle",
            type: "steps",
            description: "Calculate the missing angle",
            value: "Step 3: ∠C = 180° - 30° - 60° = 90°",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_answer",
            name: "Write Answer",
            type: "steps",
            description: "State the final answer",
            value: "Step 4: The third angle is 90°",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "triangle", to: "angle_sum", type: "has", strength: 9 },
          { from: "angle_sum", to: "angle1", type: "includes", strength: 9 },
          { from: "angle_sum", to: "angle2", type: "includes", strength: 9 },
          { from: "angle1", to: "third_angle", type: "used to find", strength: 9 },
          { from: "angle2", to: "third_angle", type: "used to find", strength: 9 },
          { from: "triangle_angle_sum_formula", to: "third_angle", type: "gives", strength: 10 },
          { from: "identify_known_angles", to: "apply_angle_sum", type: "enables", strength: 8 },
          { from: "apply_angle_sum", to: "solve_for_third_angle", type: "leads to", strength: 10 },
          { from: "solve_for_third_angle", to: "write_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'geometry_fund_04',
      problem: "Find the measure of angle x in a quadrilateral if the other three angles measure 80°, 90°, and 110°",
      problemType: "geometry",
      difficulty: "fundamental",
      description: "Quadrilateral angle sum",
      graphData: {
        nodes: [
          {
            id: "quadrilateral",
            name: "Quadrilateral",
            type: "concepts",
            description: "A polygon with four sides",
            value: "Quadrilateral ABCD",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "angle_sum",
            name: "Angle Sum",
            type: "concepts",
            description: "Sum of interior angles of a quadrilateral",
            value: "Sum = 360°",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "known_angles",
            name: "Known Angles",
            type: "values",
            description: "80°, 90°, 110°",
            value: "80° + 90° + 110° = 280°",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "angle_x",
            name: "Angle x",
            type: "values",
            description: "The angle to find",
            value: "80°",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "quadrilateral_angle_sum_formula",
            name: "Quadrilateral Angle Sum Formula",
            type: "formulas",
            description: "Sum of interior angles of a quadrilateral is 360°",
            value: "∠A + ∠B + ∠C + ∠D = 360°",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "identify_known_angles",
            name: "Identify Known Angles",
            type: "steps",
            description: "Recognize the given angles",
            value: "Step 1: ∠A = 80°, ∠B = 90°, ∠C = 110°",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "apply_angle_sum",
            name: "Apply Angle Sum Formula",
            type: "steps",
            description: "Use the quadrilateral angle sum formula",
            value: "Step 2: 80° + 90° + 110° + ∠D = 360°",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "solve_for_fourth_angle",
            name: "Solve for Fourth Angle",
            type: "steps",
            description: "Calculate the missing angle",
            value: "Step 3: ∠D = 360° - 80° - 90° - 110° = 80°",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_answer",
            name: "Write Answer",
            type: "steps",
            description: "State the final answer",
            value: "Step 4: The fourth angle is 80°",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "quadrilateral", to: "angle_sum", type: "has", strength: 9 },
          { from: "angle_sum", to: "known_angles", type: "includes", strength: 9 },
          { from: "known_angles", to: "angle_x", type: "used to find", strength: 9 },
          { from: "quadrilateral_angle_sum_formula", to: "angle_x", type: "gives", strength: 10 },
          { from: "identify_known_angles", to: "apply_angle_sum", type: "enables", strength: 8 },
          { from: "apply_angle_sum", to: "solve_for_fourth_angle", type: "leads to", strength: 10 },
          { from: "solve_for_fourth_angle", to: "write_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'geometry_fund_05',
      problem: "Find the measure of angle x if it is supplementary to a 120° angle",
      problemType: "geometry",
      difficulty: "fundamental",
      description: "Supplementary angles",
      graphData: {
        nodes: [
          {
            id: "supplementary_angles",
            name: "Supplementary Angles",
            type: "concepts",
            description: "Two angles that add up to 180°",
            value: "Supplementary angles sum to 180°",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "given_angle",
            name: "Given Angle",
            type: "values",
            description: "120°",
            value: "120°",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "angle_x",
            name: "Angle x",
            type: "values",
            description: "The angle to find",
            value: "60°",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "supplementary_angles_formula",
            name: "Supplementary Angles Formula",
            type: "formulas",
            description: "Supplementary angles add up to 180°",
            value: "∠A + ∠B = 180°",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "identify_given_angle",
            name: "Identify Given Angle",
            type: "steps",
            description: "Recognize the given angle",
            value: "Step 1: Given angle = 120°",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "apply_supplementary_formula",
            name: "Apply Supplementary Formula",
            type: "steps",
            description: "Use the supplementary angles formula",
            value: "Step 2: 120° + ∠x = 180°",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "solve_for_angle",
            name: "Solve for Angle",
            type: "steps",
            description: "Calculate the missing angle",
            value: "Step 3: ∠x = 180° - 120° = 60°",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_answer",
            name: "Write Answer",
            type: "steps",
            description: "State the final answer",
            value: "Step 4: The angle is 60°",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "supplementary_angles", to: "given_angle", type: "includes", strength: 9 },
          { from: "given_angle", to: "angle_x", type: "used to find", strength: 9 },
          { from: "supplementary_angles_formula", to: "angle_x", type: "gives", strength: 10 },
          { from: "identify_given_angle", to: "apply_supplementary_formula", type: "enables", strength: 8 },
          { from: "apply_supplementary_formula", to: "solve_for_angle", type: "leads to", strength: 10 },
          { from: "solve_for_angle", to: "write_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  intermediate: [
    {
      id: 'geometry_inter_01',
      problem: "Find the measure of angle x if two parallel lines are cut by a transversal and angle x is same-side interior to a 70° angle",
      problemType: "geometry",
      difficulty: "intermediate",
      description: "Same-side interior angles",
      graphData: {
        nodes: [
          {
            id: "parallel_lines",
            name: "Parallel Lines",
            type: "concepts",
            description: "Two lines that never intersect",
            value: "Lines l and m are parallel",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "transversal",
            name: "Transversal",
            type: "concepts",
            description: "A line that intersects two parallel lines",
            value: "Line t intersects l and m",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "same_side_interior",
            name: "Same-Side Interior Angles",
            type: "concepts",
            description: "Angles on same side of transversal, between parallel lines",
            value: "Same-side interior angles are supplementary",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "given_angle",
            name: "Given Angle",
            type: "values",
            description: "70° angle",
            value: "70°",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "angle_x",
            name: "Angle x",
            type: "values",
            description: "The angle to find",
            value: "110°",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "same_side_interior_theorem",
            name: "Same-Side Interior Angles Theorem",
            type: "formulas",
            description: "Same-side interior angles are supplementary when lines are parallel",
            value: "If l || m, then same-side interior angles are supplementary",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "identify_parallel_lines",
            name: "Identify Parallel Lines",
            type: "steps",
            description: "Recognize that the lines are parallel",
            value: "Step 1: Lines l and m are parallel",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "identify_same_side_interior",
            name: "Identify Same-Side Interior Angles",
            type: "steps",
            description: "Recognize the angle relationship",
            value: "Step 2: Angle x is same-side interior to 70° angle",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_theorem",
            name: "Apply Theorem",
            type: "steps",
            description: "Use the same-side interior angles theorem",
            value: "Step 3: Same-side interior angles are supplementary",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "find_angle",
            name: "Find Angle Measure",
            type: "steps",
            description: "Calculate the supplementary angle",
            value: "Step 4: x = 180° - 70° = 110°",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "parallel_lines", to: "transversal", type: "intersected by", strength: 9 },
          { from: "transversal", to: "same_side_interior", type: "creates", strength: 9 },
          { from: "same_side_interior", to: "given_angle", type: "related to", strength: 9 },
          { from: "given_angle", to: "angle_x", type: "supplementary to", strength: 9 },
          { from: "same_side_interior_theorem", to: "angle_x", type: "gives", strength: 10 },
          { from: "identify_parallel_lines", to: "identify_same_side_interior", type: "enables", strength: 8 },
          { from: "identify_same_side_interior", to: "apply_theorem", type: "enables", strength: 9 },
          { from: "apply_theorem", to: "find_angle", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'geometry_inter_02',
      problem: "Find the measure of angle x in an isosceles triangle if the vertex angle is 40°",
      problemType: "geometry",
      difficulty: "intermediate",
      description: "Isosceles triangle angles",
      graphData: {
        nodes: [
          {
            id: "isosceles_triangle",
            name: "Isosceles Triangle",
            type: "concepts",
            description: "Triangle with two equal sides",
            value: "Triangle with two equal sides",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "base_angles",
            name: "Base Angles",
            type: "concepts",
            description: "Angles opposite equal sides are equal",
            value: "Base angles are equal",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "vertex_angle",
            name: "Vertex Angle",
            type: "values",
            description: "40°",
            value: "40°",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "angle_sum",
            name: "Angle Sum",
            type: "concepts",
            description: "Sum of angles is 180°",
            value: "180°",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "base_angle",
            name: "Base Angle",
            type: "values",
            description: "The angle to find",
            value: "70°",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "isosceles_triangle_theorem",
            name: "Isosceles Triangle Theorem",
            type: "formulas",
            description: "Base angles of isosceles triangle are equal",
            value: "If two sides equal, then base angles equal",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "triangle_angle_sum_formula",
            name: "Triangle Angle Sum Formula",
            type: "formulas",
            description: "Sum of interior angles of a triangle is 180°",
            value: "∠A + ∠B + ∠C = 180°",
            importance: 10,
            x: 0, y: 100, z: 40
          },
          {
            id: "identify_vertex_angle",
            name: "Identify Vertex Angle",
            type: "steps",
            description: "Recognize the given vertex angle",
            value: "Step 1: Vertex angle = 40°",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "apply_isosceles_theorem",
            name: "Apply Isosceles Theorem",
            type: "steps",
            description: "Use the isosceles triangle theorem",
            value: "Step 2: Base angles are equal (let each = x)",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_angle_sum",
            name: "Apply Angle Sum Formula",
            type: "steps",
            description: "Use the triangle angle sum formula",
            value: "Step 3: 40° + x + x = 180°",
            importance: 10,
            x: 0, y: 150, z: 20
          },
          {
            id: "solve_for_base_angle",
            name: "Solve for Base Angle",
            type: "steps",
            description: "Calculate the base angle",
            value: "Step 4: 2x = 140°, x = 70°",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "isosceles_triangle", to: "base_angles", type: "has", strength: 9 },
          { from: "base_angles", to: "vertex_angle", type: "related to", strength: 9 },
          { from: "vertex_angle", to: "angle_sum", type: "used in", strength: 9 },
          { from: "angle_sum", to: "base_angle", type: "used to find", strength: 9 },
          { from: "isosceles_triangle_theorem", to: "base_angle", type: "gives", strength: 10 },
          { from: "triangle_angle_sum_formula", to: "base_angle", type: "gives", strength: 10 },
          { from: "identify_vertex_angle", to: "apply_isosceles_theorem", type: "enables", strength: 8 },
          { from: "apply_isosceles_theorem", to: "apply_angle_sum", type: "enables", strength: 9 },
          { from: "apply_angle_sum", to: "solve_for_base_angle", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'geometry_inter_03',
      problem: "Find the measure of angle x in a parallelogram if one angle measures 65°",
      problemType: "geometry",
      difficulty: "intermediate",
      description: "Parallelogram angles",
      graphData: {
        nodes: [
          {
            id: "parallelogram",
            name: "Parallelogram",
            type: "concepts",
            description: "Quadrilateral with opposite sides parallel",
            value: "Opposite sides parallel",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "opposite_angles",
            name: "Opposite Angles",
            type: "concepts",
            description: "Opposite angles are equal",
            value: "Opposite angles equal",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "consecutive_angles",
            name: "Consecutive Angles",
            type: "concepts",
            description: "Consecutive angles are supplementary",
            value: "Consecutive angles supplementary",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "given_angle",
            name: "Given Angle",
            type: "values",
            description: "65°",
            value: "65°",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "angle_x",
            name: "Angle x",
            type: "values",
            description: "The angle to find",
            value: "115°",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "parallelogram_angle_theorem",
            name: "Parallelogram Angle Theorem",
            type: "formulas",
            description: "Consecutive angles in parallelogram are supplementary",
            value: "If ABCD is parallelogram, then ∠A + ∠B = 180°",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "supplementary_angles_formula",
            name: "Supplementary Angles Formula",
            type: "formulas",
            description: "Supplementary angles add up to 180°",
            value: "∠A + ∠B = 180°",
            importance: 10,
            x: 0, y: 100, z: 40
          },
          {
            id: "identify_given_angle",
            name: "Identify Given Angle",
            type: "steps",
            description: "Recognize the given angle",
            value: "Step 1: Given angle = 65°",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "apply_parallelogram_theorem",
            name: "Apply Parallelogram Theorem",
            type: "steps",
            description: "Use the parallelogram angle theorem",
            value: "Step 2: Consecutive angles are supplementary",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_supplementary_formula",
            name: "Apply Supplementary Formula",
            type: "steps",
            description: "Use the supplementary angles formula",
            value: "Step 3: 65° + ∠x = 180°",
            importance: 10,
            x: 0, y: 150, z: 20
          },
          {
            id: "solve_for_angle",
            name: "Solve for Angle",
            type: "steps",
            description: "Calculate the missing angle",
            value: "Step 4: ∠x = 180° - 65° = 115°",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "parallelogram", to: "opposite_angles", type: "has", strength: 9 },
          { from: "parallelogram", to: "consecutive_angles", type: "has", strength: 9 },
          { from: "consecutive_angles", to: "given_angle", type: "related to", strength: 9 },
          { from: "given_angle", to: "angle_x", type: "supplementary to", strength: 9 },
          { from: "parallelogram_angle_theorem", to: "angle_x", type: "gives", strength: 10 },
          { from: "supplementary_angles_formula", to: "angle_x", type: "gives", strength: 10 },
          { from: "identify_given_angle", to: "apply_parallelogram_theorem", type: "enables", strength: 8 },
          { from: "apply_parallelogram_theorem", to: "apply_supplementary_formula", type: "enables", strength: 9 },
          { from: "apply_supplementary_formula", to: "solve_for_angle", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'geometry_inter_04',
      problem: "Find the measure of angle x in a trapezoid if the other three angles measure 60°, 120°, and 90°",
      problemType: "geometry",
      difficulty: "intermediate",
      description: "Trapezoid angle sum",
      graphData: {
        nodes: [
          {
            id: "trapezoid",
            name: "Trapezoid",
            type: "concepts",
            description: "Quadrilateral with one pair of parallel sides",
            value: "One pair of parallel sides",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "angle_sum",
            name: "Angle Sum",
            type: "concepts",
            description: "Sum of interior angles is 360°",
            value: "360°",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "known_angles",
            name: "Known Angles",
            type: "values",
            description: "60°, 120°, 90°",
            value: "60° + 120° + 90° = 270°",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "angle_x",
            name: "Angle x",
            type: "values",
            description: "The angle to find",
            value: "90°",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "quadrilateral_angle_sum_formula",
            name: "Quadrilateral Angle Sum Formula",
            type: "formulas",
            description: "Sum of interior angles of a quadrilateral is 360°",
            value: "∠A + ∠B + ∠C + ∠D = 360°",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "identify_known_angles",
            name: "Identify Known Angles",
            type: "steps",
            description: "Recognize the given angles",
            value: "Step 1: ∠A = 60°, ∠B = 120°, ∠C = 90°",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "apply_angle_sum",
            name: "Apply Angle Sum Formula",
            type: "steps",
            description: "Use the quadrilateral angle sum formula",
            value: "Step 2: 60° + 120° + 90° + ∠D = 360°",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "solve_for_fourth_angle",
            name: "Solve for Fourth Angle",
            type: "steps",
            description: "Calculate the missing angle",
            value: "Step 3: ∠D = 360° - 270° = 90°",
            importance: 10,
            x: 0, y: 150, z: 20
          },
          {
            id: "write_answer",
            name: "Write Answer",
            type: "steps",
            description: "State the final answer",
            value: "Step 4: The fourth angle is 90°",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "trapezoid", to: "angle_sum", type: "has", strength: 9 },
          { from: "angle_sum", to: "known_angles", type: "includes", strength: 9 },
          { from: "known_angles", to: "angle_x", type: "used to find", strength: 9 },
          { from: "quadrilateral_angle_sum_formula", to: "angle_x", type: "gives", strength: 10 },
          { from: "identify_known_angles", to: "apply_angle_sum", type: "enables", strength: 8 },
          { from: "apply_angle_sum", to: "solve_for_fourth_angle", type: "leads to", strength: 10 },
          { from: "solve_for_fourth_angle", to: "write_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'geometry_inter_05',
      problem: "Find the measure of angle x if it is complementary to a 35° angle",
      problemType: "geometry",
      difficulty: "intermediate",
      description: "Complementary angles",
      graphData: {
        nodes: [
          {
            id: "complementary_angles",
            name: "Complementary Angles",
            type: "concepts",
            description: "Two angles that add up to 90°",
            value: "Complementary angles sum to 90°",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "given_angle",
            name: "Given Angle",
            type: "values",
            description: "35°",
            value: "35°",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "angle_x",
            name: "Angle x",
            type: "values",
            description: "The angle to find",
            value: "55°",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "complementary_angles_formula",
            name: "Complementary Angles Formula",
            type: "formulas",
            description: "Complementary angles add up to 90°",
            value: "∠A + ∠B = 90°",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "identify_given_angle",
            name: "Identify Given Angle",
            type: "steps",
            description: "Recognize the given angle",
            value: "Step 1: Given angle = 35°",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "apply_complementary_formula",
            name: "Apply Complementary Formula",
            type: "steps",
            description: "Use the complementary angles formula",
            value: "Step 2: 35° + ∠x = 90°",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "solve_for_angle",
            name: "Solve for Angle",
            type: "steps",
            description: "Calculate the missing angle",
            value: "Step 3: ∠x = 90° - 35° = 55°",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_answer",
            name: "Write Answer",
            type: "steps",
            description: "State the final answer",
            value: "Step 4: The angle is 55°",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "complementary_angles", to: "given_angle", type: "includes", strength: 9 },
          { from: "given_angle", to: "angle_x", type: "complementary to", strength: 9 },
          { from: "complementary_angles_formula", to: "angle_x", type: "gives", strength: 10 },
          { from: "identify_given_angle", to: "apply_complementary_formula", type: "enables", strength: 8 },
          { from: "apply_complementary_formula", to: "solve_for_angle", type: "leads to", strength: 10 },
          { from: "solve_for_angle", to: "write_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  harder: [
    {
      id: 'geometry_hard_01',
      problem: "Find the measure of angle x in a triangle if one angle is twice another and the third angle is 30°",
      problemType: "geometry",
      difficulty: "harder",
      description: "Triangle with angle relationships",
      graphData: {
        nodes: [
          {
            id: "triangle",
            name: "Triangle",
            type: "concepts",
            description: "A polygon with three sides",
            value: "Triangle ABC",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "angle_relationship",
            name: "Angle Relationship",
            type: "concepts",
            description: "One angle is twice another",
            value: "Let smaller angle = x, larger angle = 2x",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "third_angle",
            name: "Third Angle",
            type: "values",
            description: "30°",
            value: "30°",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "equation",
            name: "Equation",
            type: "concepts",
            description: "x + 2x + 30° = 180°",
            value: "3x + 30° = 180°",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "angle_x",
            name: "Angle x",
            type: "values",
            description: "The angle to find",
            value: "50°",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "triangle_angle_sum_formula",
            name: "Triangle Angle Sum Formula",
            type: "formulas",
            description: "Sum of interior angles of a triangle is 180°",
            value: "∠A + ∠B + ∠C = 180°",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "identify_angle_relationship",
            name: "Identify Angle Relationship",
            type: "steps",
            description: "Recognize the angle relationship",
            value: "Step 1: Let smaller angle = x, larger angle = 2x",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "identify_third_angle",
            name: "Identify Third Angle",
            type: "steps",
            description: "Recognize the given third angle",
            value: "Step 2: Third angle = 30°",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_angle_sum",
            name: "Apply Angle Sum Formula",
            type: "steps",
            description: "Use the triangle angle sum formula",
            value: "Step 3: x + 2x + 30° = 180°",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "solve_equation",
            name: "Solve Equation",
            type: "steps",
            description: "Solve the equation for x",
            value: "Step 4: 3x + 30° = 180°, 3x = 150°, x = 50°",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "triangle", to: "angle_relationship", type: "has", strength: 9 },
          { from: "angle_relationship", to: "third_angle", type: "related to", strength: 9 },
          { from: "third_angle", to: "equation", type: "used in", strength: 9 },
          { from: "equation", to: "angle_x", type: "solves for", strength: 9 },
          { from: "triangle_angle_sum_formula", to: "angle_x", type: "gives", strength: 10 },
          { from: "identify_angle_relationship", to: "identify_third_angle", type: "enables", strength: 8 },
          { from: "identify_third_angle", to: "apply_angle_sum", type: "enables", strength: 9 },
          { from: "apply_angle_sum", to: "solve_equation", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'geometry_hard_02',
      problem: "Find the measure of angle x in a quadrilateral if three angles are in ratio 2:3:4 and the fourth angle is 60°",
      problemType: "geometry",
      difficulty: "harder",
      description: "Quadrilateral with angle ratios",
      graphData: {
        nodes: [
          {
            id: "quadrilateral",
            name: "Quadrilateral",
            type: "concepts",
            description: "A polygon with four sides",
            value: "Quadrilateral ABCD",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "angle_ratios",
            name: "Angle Ratios",
            type: "concepts",
            description: "Three angles in ratio 2:3:4",
            value: "Let angles be 2x, 3x, 4x",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "fourth_angle",
            name: "Fourth Angle",
            type: "values",
            description: "60°",
            value: "60°",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "equation",
            name: "Equation",
            type: "concepts",
            description: "2x + 3x + 4x + 60° = 360°",
            value: "9x + 60° = 360°",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "angle_x",
            name: "Angle x",
            type: "values",
            description: "The angle to find",
            value: "80°",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "quadrilateral_angle_sum_formula",
            name: "Quadrilateral Angle Sum Formula",
            type: "formulas",
            description: "Sum of interior angles of a quadrilateral is 360°",
            value: "∠A + ∠B + ∠C + ∠D = 360°",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "identify_angle_ratios",
            name: "Identify Angle Ratios",
            type: "steps",
            description: "Recognize the angle ratios",
            value: "Step 1: Let angles be 2x, 3x, 4x",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "identify_fourth_angle",
            name: "Identify Fourth Angle",
            type: "steps",
            description: "Recognize the given fourth angle",
            value: "Step 2: Fourth angle = 60°",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_angle_sum",
            name: "Apply Angle Sum Formula",
            type: "steps",
            description: "Use the quadrilateral angle sum formula",
            value: "Step 3: 2x + 3x + 4x + 60° = 360°",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "solve_equation",
            name: "Solve Equation",
            type: "steps",
            description: "Solve the equation for x",
            value: "Step 4: 9x + 60° = 360°, 9x = 300°, x = 33.33°",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "find_largest_angle",
            name: "Find Largest Angle",
            type: "steps",
            description: "Calculate the largest angle (4x)",
            value: "Step 5: Largest angle = 4x = 4(33.33°) = 133.33°",
            importance: 10,
            x: 0, y: 250, z: 0
          }
        ],
        links: [
          { from: "quadrilateral", to: "angle_ratios", type: "has", strength: 9 },
          { from: "angle_ratios", to: "fourth_angle", type: "related to", strength: 9 },
          { from: "fourth_angle", to: "equation", type: "used in", strength: 9 },
          { from: "equation", to: "angle_x", type: "solves for", strength: 9 },
          { from: "quadrilateral_angle_sum_formula", to: "angle_x", type: "gives", strength: 10 },
          { from: "identify_angle_ratios", to: "identify_fourth_angle", type: "enables", strength: 8 },
          { from: "identify_fourth_angle", to: "apply_angle_sum", type: "enables", strength: 9 },
          { from: "apply_angle_sum", to: "solve_equation", type: "leads to", strength: 10 },
          { from: "solve_equation", to: "find_largest_angle", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'geometry_hard_03',
      problem: "Find the measure of angle x if two parallel lines are cut by a transversal and angle x is vertical to a 75° angle",
      problemType: "geometry",
      difficulty: "harder",
      description: "Vertical angles",
      graphData: {
        nodes: [
          {
            id: "parallel_lines",
            name: "Parallel Lines",
            type: "concepts",
            description: "Two lines that never intersect",
            value: "Lines l and m are parallel",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "transversal",
            name: "Transversal",
            type: "concepts",
            description: "A line that intersects two parallel lines",
            value: "Line t intersects l and m",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "vertical_angles",
            name: "Vertical Angles",
            type: "concepts",
            description: "Angles formed by intersecting lines",
            value: "Vertical angles are equal",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "given_angle",
            name: "Given Angle",
            type: "values",
            description: "75°",
            value: "75°",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "angle_x",
            name: "Angle x",
            type: "values",
            description: "The angle to find",
            value: "75°",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "vertical_angles_theorem",
            name: "Vertical Angles Theorem",
            type: "formulas",
            description: "Vertical angles are equal",
            value: "If two lines intersect, vertical angles are equal",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "identify_vertical_angles",
            name: "Identify Vertical Angles",
            type: "steps",
            description: "Recognize the vertical angle relationship",
            value: "Step 1: Angle x is vertical to 75° angle",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "apply_vertical_angles_theorem",
            name: "Apply Vertical Angles Theorem",
            type: "steps",
            description: "Use the vertical angles theorem",
            value: "Step 2: Vertical angles are equal",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "find_angle",
            name: "Find Angle Measure",
            type: "steps",
            description: "Set the angles equal",
            value: "Step 3: x = 75°",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_answer",
            name: "Write Answer",
            type: "steps",
            description: "State the final answer",
            value: "Step 4: The angle is 75°",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "parallel_lines", to: "transversal", type: "intersected by", strength: 9 },
          { from: "transversal", to: "vertical_angles", type: "creates", strength: 9 },
          { from: "vertical_angles", to: "given_angle", type: "related to", strength: 9 },
          { from: "given_angle", to: "angle_x", type: "equals", strength: 10 },
          { from: "vertical_angles_theorem", to: "angle_x", type: "gives", strength: 10 },
          { from: "identify_vertical_angles", to: "apply_vertical_angles_theorem", type: "enables", strength: 8 },
          { from: "apply_vertical_angles_theorem", to: "find_angle", type: "enables", strength: 9 },
          { from: "find_angle", to: "write_answer", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'geometry_hard_04',
      problem: "Find the measure of angle x in a rhombus if one angle measures 120°",
      problemType: "geometry",
      difficulty: "harder",
      description: "Rhombus angles",
      graphData: {
        nodes: [
          {
            id: "rhombus",
            name: "Rhombus",
            type: "concepts",
            description: "Parallelogram with all sides equal",
            value: "All sides equal",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "opposite_angles",
            name: "Opposite Angles",
            type: "concepts",
            description: "Opposite angles are equal",
            value: "Opposite angles equal",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "consecutive_angles",
            name: "Consecutive Angles",
            type: "concepts",
            description: "Consecutive angles are supplementary",
            value: "Consecutive angles supplementary",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "given_angle",
            name: "Given Angle",
            type: "values",
            description: "120°",
            value: "120°",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "angle_x",
            name: "Angle x",
            type: "values",
            description: "The angle to find",
            value: "60°",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "rhombus_angle_theorem",
            name: "Rhombus Angle Theorem",
            type: "formulas",
            description: "Consecutive angles in rhombus are supplementary",
            value: "If ABCD is rhombus, then ∠A + ∠B = 180°",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "supplementary_angles_formula",
            name: "Supplementary Angles Formula",
            type: "formulas",
            description: "Supplementary angles add up to 180°",
            value: "∠A + ∠B = 180°",
            importance: 10,
            x: 0, y: 100, z: 40
          },
          {
            id: "identify_given_angle",
            name: "Identify Given Angle",
            type: "steps",
            description: "Recognize the given angle",
            value: "Step 1: Given angle = 120°",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "apply_rhombus_theorem",
            name: "Apply Rhombus Theorem",
            type: "steps",
            description: "Use the rhombus angle theorem",
            value: "Step 2: Consecutive angles are supplementary",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_supplementary_formula",
            name: "Apply Supplementary Formula",
            type: "steps",
            description: "Use the supplementary angles formula",
            value: "Step 3: 120° + ∠x = 180°",
            importance: 10,
            x: 0, y: 150, z: 20
          },
          {
            id: "solve_for_angle",
            name: "Solve for Angle",
            type: "steps",
            description: "Calculate the missing angle",
            value: "Step 4: ∠x = 180° - 120° = 60°",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "rhombus", to: "opposite_angles", type: "has", strength: 9 },
          { from: "rhombus", to: "consecutive_angles", type: "has", strength: 9 },
          { from: "consecutive_angles", to: "given_angle", type: "related to", strength: 9 },
          { from: "given_angle", to: "angle_x", type: "supplementary to", strength: 9 },
          { from: "rhombus_angle_theorem", to: "angle_x", type: "gives", strength: 10 },
          { from: "supplementary_angles_formula", to: "angle_x", type: "gives", strength: 10 },
          { from: "identify_given_angle", to: "apply_rhombus_theorem", type: "enables", strength: 8 },
          { from: "apply_rhombus_theorem", to: "apply_supplementary_formula", type: "enables", strength: 9 },
          { from: "apply_supplementary_formula", to: "solve_for_angle", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'geometry_hard_05',
      problem: "Find the measure of angle x in a kite if one angle measures 100° and the opposite angle measures 80°",
      problemType: "geometry",
      difficulty: "harder",
      description: "Kite angles",
      graphData: {
        nodes: [
          {
            id: "kite",
            name: "Kite",
            type: "concepts",
            description: "Quadrilateral with two pairs of adjacent equal sides",
            value: "Two pairs of adjacent equal sides",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "angle_sum",
            name: "Angle Sum",
            type: "concepts",
            description: "Sum of interior angles is 360°",
            value: "360°",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "known_angles",
            name: "Known Angles",
            type: "values",
            description: "100°, 80°",
            value: "100° + 80° = 180°",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "angle_x",
            name: "Angle x",
            type: "values",
            description: "The angle to find",
            value: "90°",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "quadrilateral_angle_sum_formula",
            name: "Quadrilateral Angle Sum Formula",
            type: "formulas",
            description: "Sum of interior angles of a quadrilateral is 360°",
            value: "∠A + ∠B + ∠C + ∠D = 360°",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "identify_known_angles",
            name: "Identify Known Angles",
            type: "steps",
            description: "Recognize the given angles",
            value: "Step 1: ∠A = 100°, ∠C = 80°",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "apply_angle_sum",
            name: "Apply Angle Sum Formula",
            type: "steps",
            description: "Use the quadrilateral angle sum formula",
            value: "Step 2: 100° + ∠B + 80° + ∠D = 360°",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "solve_for_remaining_angles",
            name: "Solve for Remaining Angles",
            type: "steps",
            description: "Calculate the missing angles",
            value: "Step 3: ∠B + ∠D = 360° - 180° = 180°",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "find_angle_x",
            name: "Find Angle x",
            type: "steps",
            description: "Calculate angle x (assuming it's one of the remaining angles)",
            value: "Step 4: ∠x = 90° (if ∠B = ∠D = 90°)",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "kite", to: "angle_sum", type: "has", strength: 9 },
          { from: "angle_sum", to: "known_angles", type: "includes", strength: 9 },
          { from: "known_angles", to: "angle_x", type: "used to find", strength: 9 },
          { from: "quadrilateral_angle_sum_formula", to: "angle_x", type: "gives", strength: 10 },
          { from: "identify_known_angles", to: "apply_angle_sum", type: "enables", strength: 8 },
          { from: "apply_angle_sum", to: "solve_for_remaining_angles", type: "leads to", strength: 10 },
          { from: "solve_for_remaining_angles", to: "find_angle_x", type: "leads to", strength: 10 }
        ]
      }
    }
  ]
}; 